"use client";

import { Skeleton } from "@/components/ui/skeleton";
import { Icon } from "@/components/ui/icon";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { ListingStatusBadge } from "@/components/listings/listing-status-badge";
import { ListingPreviewInfo } from "@/components/listings/listing-preview-info";
import { ListingPriceChangeBadge } from "@/components/listings/listing-price-change-badge";
import { api } from "@/lib/api-client";
import { buildImageCDNUrl } from "@repo/image-transformations";
import { buildBusinessTypeImageCDNAssetUrl } from "@/lib/url";
import { prettyNumber } from "@/lib/string-utils";
import { getTotalAskingPrice } from "@/lib/listing-utils";
import { useFormatter, useTranslations } from "next-intl";
import Image from "@/components/common/image";
import listingImagePlaceholder from "@/components/listings/assets/listing-placeholder.png";
import type { ListingInfoAttribute } from "@repo/utils";
import { useMemo } from "react";

function isValidFinancial(
  val: number | bigint | null | undefined,
): val is number | bigint {
  if (val == null) return false;
  const n = typeof val === "bigint" ? Number(val) : val;
  return Number.isFinite(n) && n > 1;
}

export function MapListingCard({
  slug,
  enabled,
  horizontal = false,
  boostedAttributes,
}: {
  slug: string;
  enabled?: boolean;
  horizontal?: boolean;
  boostedAttributes?: ListingInfoAttribute[];
}) {
  const t = useTranslations();
  const format = useFormatter();

  const { data, isLoading } = api.listings.getListingBySlug.useQuery(
    { slug, trackView: false },
    {
      enabled,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  const listing = data?.listing ?? null;

  const shortAddress = listing?.business?.address
    ? `${listing.business.address.city ? `${listing.business.address.city}, ` : ""}${listing.business.address.state}`
    : null;

  const isAssetSale = listing?.saleType === "ASSET_SALE";

  const financialMetrics = useMemo(() => {
    if (!listing) return [];
    const metrics: { label: string; value: string }[] = [];

    if (isAssetSale) {
      if (isValidFinancial(listing.inventory)) {
        metrics.push({
          label: t("saas.listingPreview.info.inventoryLabel"),
          value: `$${prettyNumber(listing.inventory)}`,
        });
      }
      if (isValidFinancial(listing.ffande)) {
        metrics.push({
          label: t("saas.listingPreview.info.ffandeLabel"),
          value: `$${prettyNumber(listing.ffande)}`,
        });
      }
    } else {
      if (isValidFinancial(listing.revenue)) {
        metrics.push({
          label: t("saas.listingPreview.info.revenueLabel"),
          value: `$${prettyNumber(listing.revenue)}`,
        });
      }
      if (isValidFinancial(listing.sde)) {
        metrics.push({
          label: t("saas.listingPreview.info.sdeLabel"),
          value: `$${prettyNumber(listing.sde)}`,
        });
      }
      if (isValidFinancial(listing.ebitda)) {
        metrics.push({
          label: t("saas.listingPreview.info.ebitdaLabel"),
          value: `$${prettyNumber(listing.ebitda)}`,
        });
      }
      if (isValidFinancial(listing.profit)) {
        metrics.push({
          label: t("saas.listingPreview.info.profitLabel"),
          value: `$${prettyNumber(listing.profit)}`,
        });
      }
    }

    return metrics.slice(0, 2);
  }, [listing, isAssetSale, t]);

  const qualifierBadges = useMemo(() => {
    if (!listing) return [];
    const badges: string[] = [];
    if (listing.sbaPrequalified)
      badges.push(t("saas.listingPreview.info.sbaBadge"));
    if (listing.financingAvailable)
      badges.push(t("saas.listingPreview.info.financingBadge"));
    if (listing.propertyIncludedInPrice)
      badges.push(t("saas.listingPreview.info.propertyBadge"));
    return badges.slice(0, 2);
  }, [listing, t]);

  if (isLoading || !enabled) {
    return <MapListingCardSkeleton horizontal={horizontal} />;
  }

  if (!listing) return null;

  const imageKey = listing.listingMedia[0]?.image?.file.key;
  const imageUrl = imageKey ? buildImageCDNUrl(imageKey, "thumbnail") : null;
  const placeholderImageKey = listing.placeholderImage?.file.key;
  const placeholderImageUrl = placeholderImageKey
    ? buildImageCDNUrl(placeholderImageKey, "thumbnail")
    : null;
  const imageBusinessTypeFallback = listing.businessType
    ? buildImageCDNUrl(
        buildBusinessTypeImageCDNAssetUrl(listing.businessType.slug),
        "thumbnail",
      )
    : null;

  const finalPrice = getTotalAskingPrice(listing);

  const imageSrc =
    imageUrl ??
    placeholderImageUrl ??
    imageBusinessTypeFallback ??
    listingImagePlaceholder;

  const title = listing.pitch ?? t("saas.listingPreview.title.untitled");

  const formattedPrice =
    finalPrice > 0
      ? format.number(finalPrice, {
          style: "currency",
          currency: listing.currency,
          minimumFractionDigits: 0,
        })
      : t("common.negotiable");

  if (horizontal) {
    return (
      <article className="flex flex-col">
        <div className="relative h-[120px] overflow-hidden bg-muted">
          <Image
            src={imageSrc}
            alt={title}
            fill
            className="object-cover"
            sizes="(max-width: 768px) 100vw, 400px"
          />
        </div>

        <div className="flex flex-col gap-1.5 p-3.5">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground min-w-0">
            {listing.businessType && (
              <>
                <Icon
                  icon={
                    (listing.businessType.iconName
                      ? `BusinessType.${listing.businessType.iconName}`
                      : "Building2Solid") as never
                  }
                  className="size-3.5 shrink-0"
                  cdnBusinessTypeIconVariant="muted"
                />
                <span className="truncate">{listing.businessType.name}</span>
              </>
            )}
            {listing.businessType && shortAddress && (
              <span className="text-muted-foreground">·</span>
            )}
            {shortAddress && <span className="truncate">{shortAddress}</span>}
          </div>

          <h3 className="text-[15px] font-semibold leading-snug line-clamp-2">
            {title}
          </h3>

          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-base font-bold font-data">
                {formattedPrice}
              </span>
              <ListingPriceChangeBadge
                priceDirection={listing.priceDirection}
                priceChangePercent={listing.priceChangePercent}
                visible={listing.showPriceHistory}
                compact
              />
              <ListingStatusBadge listing={listing} small />
            </div>

          </div>

          {(financialMetrics.length > 0 || qualifierBadges.length > 0) && (
            <div className="flex items-center gap-2 pt-0.5">
              {financialMetrics.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {financialMetrics.map((metric, i) => (
                    <span key={metric.label}>
                      {i > 0 && <span className="mx-1 text-muted-foreground">·</span>}
                      <span>{metric.label} </span>
                      <span className="font-data font-semibold text-foreground">
                        {metric.value}
                      </span>
                    </span>
                  ))}
                </p>
              )}

              {qualifierBadges.length > 0 && financialMetrics.length > 0 && (
                <span className="text-muted-foreground">·</span>
              )}

              {qualifierBadges.length > 0 && (
                <div className="flex gap-1.5">
                  {qualifierBadges.map((badge) => (
                    <span
                      key={badge}
                      className="inline-flex shrink-0 items-center rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground"
                    >
                      {badge}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </article>
    );
  }

  return (
    <article className="flex flex-col">
      <div className="relative rounded-lg overflow-hidden">
        <AspectRatio ratio={16 / 10}>
          <Image
            src={imageSrc}
            alt={title}
            fill
            className="object-cover"
            sizes="300px"
          />
        </AspectRatio>
      </div>

      <div className="flex flex-col gap-1 pt-2.5 px-1 pb-1">
        <div className="flex items-center gap-1 text-[11px] text-muted-foreground min-w-0">
          {listing.businessType && (
            <>
              <Icon
                icon={
                  (listing.businessType.iconName
                    ? `BusinessType.${listing.businessType.iconName}`
                    : "Building2Solid") as never
                }
                className="size-3 shrink-0"
                cdnBusinessTypeIconVariant="muted"
              />
              <span className="truncate">{listing.businessType.name}</span>
            </>
          )}
          {listing.businessType && shortAddress && (
            <span className="text-muted-foreground">·</span>
          )}
          {shortAddress && (
            <>
              <Icon icon="MarkerPinSolid" className="size-3 shrink-0" />
              <span className="truncate">{shortAddress}</span>
            </>
          )}
        </div>

        <h3 className="text-sm font-semibold leading-snug line-clamp-2">
          {title}
        </h3>

        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-bold font-data">{formattedPrice}</span>
          <ListingPriceChangeBadge
            priceDirection={listing.priceDirection}
            priceChangePercent={listing.priceChangePercent}
            visible={listing.showPriceHistory}
            compact
          />
          <ListingStatusBadge listing={listing} small />
        </div>

        <ListingPreviewInfo
          listing={listing}
          boostedAttributes={boostedAttributes}
        />
      </div>
    </article>
  );
}

function MapListingCardSkeleton({
  horizontal = false,
}: {
  horizontal?: boolean;
}) {
  if (horizontal) {
    return (
      <div className="flex flex-col">
        <Skeleton className="h-[120px] w-full" />
        <div className="flex flex-col gap-2 p-3.5">
          <Skeleton className="w-32 h-3" />
          <Skeleton className="w-3/4 h-4" />
          <Skeleton className="w-1/3 h-5" />
          <Skeleton className="w-1/2 h-3" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <Skeleton
        className="w-full rounded-lg"
        style={{ aspectRatio: "16/10" }}
      />
      <div className="flex flex-col gap-1 pt-2.5 px-1 pb-1">
        <Skeleton className="w-32 h-2.5" />
        <Skeleton className="w-full h-4" />
        <Skeleton className="w-1/3 h-4" />
      </div>
    </div>
  );
}

MapListingCard.displayName = "MapListingCard";
